// Транспорт и хуки раздела «Очередь уведомлений» (bpe_outbox движка процессов).
// ТОЛЬКО ЧТЕНИЕ, и это свойство ядра, а не вежливость фронта: ручек записи в /notify
// не существует.
//
// ТЕЛА УВЕДОМЛЕНИЙ ЗДЕСЬ НЕТ И НЕ БУДЕТ. Ядро не отдаёт body ни одной ручкой /notify:
// в теле — текст чужого задания. Раздел отвечает на вопрос «дошло или застряло», и для
// него достаточно канала, адресатов, времени и состояния. Появись здесь поле body —
// это не «дополнили экран», а дыра, и её надо закрывать в ядре (notify_api.py).

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'

const БАЗА = '/api/notify'

/** Тот же разбор ответа, что в api/bp.ts, api/p73.ts и api/p73x.ts. */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    // 400 от ядра — отказ по существу фильтра. Пустая таблица читалась бы как
    // «очередь пуста», а это ложь ровно там, где раздел и заводился.
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

function строкаЗапроса(ф: Record<string, unknown>): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(ф)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    p.set(k, v === true ? '1' : String(v))
  }
  const s = p.toString()
  return s ? '?' + s : ''
}

const ОБЩЕЕ = { staleTime: 60_000, retry: 1 } as const

// ─────────── формы ответов ядра (см. notify_api.py) ───────────

export type Статус =
  | 'на_экране' | 'отправлено' | 'ждёт' | 'ждёт_настройки' | 'застряло' | 'недостижим'

export interface Адресат {
  /** Как адресата назвал движок: номер учётки или константа вроде группового чата. */
  who: number | string
  /** Фамилия из зеркала. null — это не человек из bx_users (константа канала). */
  name: string | null
  active: boolean | null
}

export interface СтрокаОчереди {
  id: number
  at: string | null
  channel: string | null
  to: Адресат[]
  instance_id: number | null
  task_id: number | null
  sent_at: string | null
  error: string | null
  attempts: number
  status: Статус
  next_try_at: string | null
  delivered_n: number
  waiting_for_vars: string[] | null
}

export interface КаналСчёт {
  channel: string | null
  total: number
  screen: number
  sent: number
  stuck: number
  unreachable: number
  config_wait: number
  waiting: number
  first_at: string | null
  last_at: string | null
  last_sent_at: string | null
}

export interface АдреснаяСводка {
  total: number
  with_chat_id: number
  no_chat_id: number
  only_nick: number
  with_email: number
  no_email: number
}

export interface NotifyRefs {
  channels: КаналСчёт[]
  totals: {
    total: number
    screen: number
    sent: number
    stuck: number
    unreachable: number
    config_wait: number
    waiting: number
  }
  /** Боевая отправка (NOTIFY_LIVE). Пока false — разборщик считает всухую. */
  live: boolean
  /** Только ИМЕНА переменных окружения. Значений ядро не отдаёт никому. */
  vars_named: string[]
  vars_known: Record<string, string>
  send_channels: string[]
  attempts_max: number
  addresses: { active?: АдреснаяСводка; inactive?: АдреснаяСводка }
  status_names: Record<string, string>
  caveats: string[]
}

export interface ОчередьОтвет {
  rows: СтрокаОчереди[]
  total: number
  next: number | null
  limit: number
  start: number
  kind: string
  note: string
}

export function useNotifyRefs() {
  return useQuery<NotifyRefs>({
    queryKey: ['notify', 'refs'],
    queryFn: () => запрос('/refs'),
    ...ОБЩЕЕ,
  })
}

export function useОчередь(ф: Record<string, unknown>) {
  return useQuery<ОчередьОтвет>({
    queryKey: ['notify', 'queue', ф],
    queryFn: () => запрос('/queue' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}
